import { TokenAmount } from "@/components/brand/TokenAmount";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { AnimatedLTVBar } from "@/components/defi/AnimatedLTVBar";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import { VaultsSection } from "@/components/home/VaultsSection";
import { Pagination } from "@/components/ui/Pagination";
import { CollateralLines } from "@/components/vault/CollateralLines";
import {
	riskBandOrder,
	rowBgForBand,
	VaultFiltersBar,
	type BandFilter,
} from "@/components/vault/VaultFiltersBar";
import { VaultRiskBadge } from "@/components/vault/VaultRiskBadge";
import {
	VaultViewToggle,
	type VaultViewMode,
} from "@/components/vault/VaultViewToggle";
import {
	useGetPublicPriceFeeds,
	useGetPublicVaults,
	useProtocolRiskParams,
	toRiskParams,
	type PublicVaultSortKey,
	type PublicVaultUi,
} from "@/hooks/usePublicProtocol";
import { useAuth } from "@/lib/auth";
import { hasProtocolEnv } from "@/lib/protocol-actor";
import { docsArticlePath } from "@/lib/docs/parse";
import { ROUTES } from "@/lib/routes";
import {
	classifyVaultRisk,
	formatCr,
	riskLegend,
	type ProtocolRiskParams,
} from "@/lib/vault-risk";
import { InfoExplain } from "@/components/ui/InfoExplain";
import { cn, formatNum } from "@/lib/utils";
import type { CollateralKey } from "@/data/fixtures";
import { ExternalLink, Gavel } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const PAGE = 12;

function formatCreated(ns?: bigint): string {
	if (ns == null || ns <= 0n) return "—";
	const ms = Number(ns / 1_000_000n);
	if (!Number.isFinite(ms) || ms <= 0) return "—";
	return new Date(ms).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function PublicVaultTile({
	vault,
	list,
	prices,
	params,
	onClick,
}: {
	vault: PublicVaultUi;
	list: boolean;
	prices: Record<string, number>;
	params: ProtocolRiskParams;
	onClick: () => void;
}) {
	const risk = classifyVaultRisk(vault.collateralValueUsd, vault.debt, params);
	const lines = vault.collateral.map(([asset, amount]) => {
		const priceUsd = prices[asset] ?? 0;
		return {
			asset,
			amount,
			priceUsd,
			valueUsd: amount * priceUsd,
		};
	});

	return (
		<article
			role="button"
			tabIndex={0}
			onClick={onClick}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") onClick();
			}}
			className={cn(
				"card-surface cursor-pointer text-left transition hover:border-primary/50",
				list ? "space-y-3 p-5" : "flex h-full flex-col gap-3 p-5",
				risk.borderClass,
				risk.bgClass,
			)}
		>
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="font-mono text-lg font-bold">
						Vault #{vault.id.toString()}
					</p>
					<p className="truncate font-mono text-xs text-muted-foreground">
						{vault.ownerLabel}
					</p>
				</div>
				<VaultRiskBadge meta={risk} size="lg" />
			</div>

			<div>
				<p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
					Collateral locked
				</p>
				<CollateralLines lines={lines} compact={list} />
			</div>

			{/* Oracle + debt only — LTV/CR live on the progress bar below (no duplicate row). */}
			<div
				className={cn(
					"grid gap-3 border-t border-border/50 pt-3",
					list ? "grid-cols-2 sm:grid-cols-2" : "grid-cols-2",
				)}
			>
				<div>
					<p className="text-[10px] font-semibold uppercase text-muted-foreground">
						Oracle total
					</p>
					<p className="font-mono-amount text-base font-bold">
						$
						<AnimatedNumber value={vault.collateralValueUsd} decimals={0} />
					</p>
				</div>
				<div className="min-w-0">
					<p className="text-[10px] font-semibold uppercase text-muted-foreground">
						Debt
					</p>
					<p className="truncate font-mono-amount text-base font-bold tabular-nums">
						{formatNum(vault.debt, vault.debt >= 100 ? 0 : 2)}{" "}
						<span className="text-xs font-semibold text-muted-foreground">
							USDM
						</span>
					</p>
				</div>
			</div>

			{(vault.debt > 0 || vault.collateralValueUsd > 0) && (
				<AnimatedLTVBar
					ltv={vault.ltv}
					healthFactor={vault.healthFactor}
					variant="compact"
					showLabels
				/>
			)}
		</article>
	);
}

export default function VaultsPage() {
	const navigate = useNavigate();
	const { principal, isAuthenticated } = useAuth();
	const [sort, setSort] = useState<PublicVaultSortKey>("healthAsc");
	const [offset, setOffset] = useState(0);
	const [view, setView] = useState<VaultViewMode>("list");
	const [band, setBand] = useState<BandFilter>("all");
	const { data, isLoading, isError, error } = useGetPublicVaults(
		PAGE,
		offset,
		sort,
	);
	const { data: priceData } = useGetPublicPriceFeeds();
	const { data: riskData } = useProtocolRiskParams();
	const riskParams = toRiskParams(riskData);
	const legend = riskLegend(riskParams);
	const prices = priceData?.prices ?? { ICP: 0, ckBTC: 0, ckETH: 0 };
	const envOk = hasProtocolEnv();

	const vaults = data?.vaults ?? [];
	const total = data?.total ?? 0;
	const source = data?.source ?? "empty";

	const filtered = useMemo(() => {
		let list = vaults.map((v) => {
			const r = classifyVaultRisk(v.collateralValueUsd, v.debt, riskParams);
			return { v, r };
		});
		if (band !== "all") {
			list = list.filter(({ r }) => r.band === band);
		}
		list.sort((a, b) => {
			const d = riskBandOrder(a.r.band) - riskBandOrder(b.r.band);
			if (d !== 0) return d;
			return a.r.cr - b.r.cr;
		});
		return list.map(({ v }) => v);
	}, [vaults, band, riskParams]);

	return (
		<div className="container mx-auto space-y-10 px-4 py-6 sm:py-8">
			<div id="section-your-vaults">
				<p className="section-eyebrow">Vaults</p>
				<h1 className="text-xl font-bold tracking-tight sm:text-2xl">
					Manage positions
				</h1>
				<p className="mt-1 max-w-xl text-sm text-muted-foreground">
					Create and manage vaults. Explorer lists all open positions.
				</p>
				<p className="mt-1 text-xs">
					<Link
						to={docsArticlePath("protocol", "vaults")}
						className="font-semibold text-primary underline"
					>
						Docs · how it works
					</Link>
				</p>
			</div>

			<VaultsSection />

			<section className="space-y-4 border-t border-border pt-8">
				<div className="flex flex-wrap items-end justify-between gap-3">
					<div>
						<p className="section-eyebrow">Public explorer</p>
						<h2 className="text-lg font-bold">All protocol vaults</h2>
						<p className="text-sm text-muted-foreground">
							{PAGE} per page · {source === "live" ? "live" : "no data"}
						</p>
					</div>
					<VaultViewToggle view={view} onChange={setView} />
				</div>

				<div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
					{legend.map((r) => (
						<span
							key={r.badge}
							className={cn(
								"rounded-full border px-2 py-0.5",
								r.badgeClass,
							)}
						>
							<strong>{r.badge}</strong> · {r.range}
						</span>
					))}
				</div>

				<VaultFiltersBar
					band={band}
					onBandChange={(b) => {
						setBand(b);
						setOffset(0);
					}}
					sort={sort}
					onSortChange={(s) => {
						setSort(s);
						setOffset(0);
					}}
					showSort
				/>

				{!envOk ? (
					<p className="py-8 text-center text-sm text-muted-foreground">
						Protocol not configured — public vaults unavailable.
					</p>
				) : isLoading ? (
					<div
						className={cn(
							"grid gap-3",
							view === "grid"
								? "sm:grid-cols-2 lg:grid-cols-3"
								: "grid-cols-1",
						)}
					>
						{Array.from({ length: 4 }).map((_, i) => (
							<div key={i} className="skeleton-shimmer h-52" />
						))}
					</div>
				) : isError ? (
					<p className="py-8 text-center text-sm text-danger">
						{error instanceof Error ? error.message : "Failed to load vaults"}
					</p>
				) : filtered.length === 0 ? (
					<p className="py-8 text-center text-sm text-muted-foreground">
						No vaults in this filter.
					</p>
				) : view === "list" ? (
					<div className="card-surface overflow-x-auto">
						<table className="w-full min-w-[960px] text-left text-sm">
							<thead>
								<tr className="border-b border-border text-[11px] uppercase text-muted-foreground">
									<th className="px-3 py-2 font-semibold">Vault</th>
									<th className="px-3 py-2 font-semibold">Created</th>
									<th className="px-3 py-2 font-semibold">Collateral locked</th>
									<th className="px-3 py-2 font-semibold">Value (USD)</th>
									<th className="px-3 py-2 font-semibold">Debt</th>
									<th className="relative px-3 py-2 font-semibold">
										<InfoExplain label="LTV" title="Loan to value (LTV)">
											<p>
												Debt as a percent of collateral value. Higher LTV =
												more risk.
											</p>
										</InfoExplain>
									</th>
									<th className="relative px-3 py-2 font-semibold">
										<InfoExplain
											label="CR"
											title="Collateral ratio (CR)"
										>
											<p>
												How many dollars of collateral back each $1 of USDM
												debt (collateral ÷ debt). Higher is safer. Same as
												protocol health factor.
											</p>
											<p>
												Healthy / Caution around mint floor{" "}
												{riskParams.minCollateralRatio.toFixed(2)}×. At risk
												down to {riskParams.liquidationThreshold.toFixed(2)}×
												(orange). Liquidatable below that (red); underwater
												below 1.00× (purple).
											</p>
										</InfoExplain>
									</th>
									<th className="px-3 py-2 font-semibold">Status</th>
									<th className="px-3 py-2 font-semibold">Owner</th>
									<th className="px-3 py-2 font-semibold">Open</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((v) => {
									const risk = classifyVaultRisk(
										v.collateralValueUsd,
										v.debt,
										riskParams,
									);
									const isYours =
										isAuthenticated &&
										!!principal &&
										!!v.owner &&
										v.owner === principal;
									const canLiq =
										!isYours &&
										(risk.band === "liquidatable" ||
											risk.band === "underwater");
									return (
										<tr
											key={String(v.id)}
											className={cn(
												"cursor-pointer border-b border-border/40 transition hover:brightness-95",
												rowBgForBand(risk.band),
											)}
											onClick={() => navigate(ROUTES.vaultDetail(v.id))}
										>
											<td className="px-3 py-2 font-mono text-xs font-bold">
												#{v.id.toString()}
											</td>
											<td className="px-3 py-2 text-xs text-muted-foreground">
												{formatCreated(v.createdNs ?? v.lastUpdatedNs)}
											</td>
											<td className="max-w-[12rem] px-3 py-2 text-xs">
												<div className="flex items-center gap-1.5">
													{v.collateral.map(([a]) => (
														<TokenIcon key={a} token={a} size="sm" plain />
													))}
													<span className="truncate">
														{v.collateral
															.map(
																([a, amt]) =>
																	`${formatNum(amt, a === "ckBTC" ? 4 : 2)} ${a}`,
															)
															.join(" · ") || "—"}
													</span>
												</div>
											</td>
											<td className="px-3 py-2 font-mono-amount text-xs font-semibold">
												${formatNum(v.collateralValueUsd, 0)}
											</td>
											<td className="px-3 py-2">
												<TokenAmount
													id="USDM"
													value={v.debt}
													decimals={2}
													size="sm"
												/>
											</td>
											<td className="px-3 py-2 font-mono-amount text-xs">
												{formatNum(v.ltv, 1)}%
											</td>
											<td
												className={cn(
													"px-3 py-2 font-mono-amount text-xs font-bold",
													risk.factorClass,
												)}
											>
												{formatCr(risk.cr)}
											</td>
											<td className="px-3 py-2">
												<VaultRiskBadge
													meta={risk}
													size="sm"
													showFactor={false}
												/>
											</td>
											<td className="px-3 py-2">
												{isYours ? (
													<span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">
														Yours
													</span>
												) : (
													<span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
														Others
													</span>
												)}
											</td>
											<td className="px-3 py-2">
												<div className="flex flex-wrap gap-1.5">
													<button
														type="button"
														className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-border bg-background/80 px-2.5 text-[11px] font-bold hover:border-primary hover:text-primary"
														onClick={(e) => {
															e.stopPropagation();
															navigate(ROUTES.vaultDetail(v.id));
														}}
													>
														<ExternalLink className="size-3.5" aria-hidden />
														Details
													</button>
													{canLiq ? (
														<button
															type="button"
															className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-danger/40 bg-danger/10 px-2.5 text-[11px] font-bold text-danger"
															onClick={(e) => {
																e.stopPropagation();
																navigate(
																	`${ROUTES.liquidation}?vaultId=${v.id.toString()}`,
																);
															}}
														>
															<Gavel className="size-3.5" aria-hidden />
															{risk.band === "underwater"
																? "Dutch"
																: "Liquidate"}
														</button>
													) : null}
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				) : (
					<ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{filtered.map((v) => (
							<li key={String(v.id)}>
								<PublicVaultTile
									params={riskParams}
									vault={v}
									list={false}
									prices={prices as Record<CollateralKey, number>}
									onClick={() => navigate(ROUTES.vaultDetail(v.id))}
								/>
							</li>
						))}
					</ul>
				)}


				{total > 0 && band === "all" ? (
					<Pagination
						offset={offset}
						pageSize={PAGE}
						total={total}
						onOffsetChange={setOffset}
					/>
				) : null}
			</section>

		</div>
	);
}
